import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { client } from "@/lib/api";
import { Loader2, Lock, Mail } from "lucide-react";

/**
 * Email capture modal that opens before report generation.
 * - Fetches quota for the entered email
 * - If quota exceeded, shows a paywall card
 * - Otherwise triggers `onConfirm({ email })` — parent creates the report
 */
export default function EmailCaptureDialog({ open, onOpenChange, onConfirm, generating }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [quota, setQuota] = useState(null); // { used, limit, remaining } or null
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
  }, [open]);

  async function refreshQuota(next) {
    setEmail(next);
    setError(null);
    if (!next || !next.includes("@")) { setQuota(null); return; }
    try {
      const r = await client.get(`/quota?email=${encodeURIComponent(next)}`);
      setQuota(r.data);
    } catch { setQuota(null); }
  }

  async function submit() {
    if (!email || !email.includes("@")) { setError("Please enter a valid email address."); return; }
    onConfirm({ email });
  }

  function skip() {
    onConfirm({ email: "" });
  }

  const paywalled = quota && quota.remaining <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="email-capture-dialog" className="max-w-md rounded-sm border-slate-200">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium">
            {paywalled ? t("paywall.title") : t("email.title")}
          </DialogTitle>
          <DialogDescription className="text-slate-600 pt-2">
            {paywalled
              ? t("paywall.body", { used: quota.used, limit: quota.limit })
              : t("email.body")}
          </DialogDescription>
        </DialogHeader>

        {!paywalled && (
          <div className="space-y-4 pt-2">
            <div className="relative">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="email-capture-input"
                type="email"
                placeholder={t("email.placeholder")}
                value={email}
                onChange={(e) => refreshQuota(e.target.value)}
                className="pl-9 rounded-sm border-slate-200"
              />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            {quota && (
              <p data-testid="quota-line" className="text-xs text-slate-500 mono">
                {t("email.quota", { remaining: quota.remaining, limit: quota.limit })}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                data-testid="email-capture-confirm"
                onClick={submit}
                disabled={generating || !email}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm flex-1"
              >
                {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("calc.generating")}</>) : t("email.confirm")}
              </Button>
              <Button
                data-testid="email-capture-skip"
                variant="outline"
                onClick={skip}
                disabled={generating}
                className="rounded-sm border-slate-200"
              >
                {t("email.skip")}
              </Button>
            </div>
          </div>
        )}

        {paywalled && (
          <div className="space-y-4 pt-2" data-testid="paywall-card">
            <div className="border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
              <Lock className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800 leading-relaxed">
                <div className="font-semibold mb-1">Free tier: {quota.used} / {quota.limit} used</div>
                Contact sales to unlock unlimited reports, PDF export, and workspace features.
              </div>
            </div>
            <div className="flex gap-3">
              <a
                data-testid="paywall-contact-sales"
                href="mailto:sales@pr-impact.dash?subject=Upgrade%20request"
                className="flex-1 text-center bg-slate-900 hover:bg-slate-800 text-white rounded-sm py-2 text-sm font-medium"
              >
                {t("paywall.cta")}
              </a>
              <Button
                data-testid="paywall-back"
                variant="outline"
                onClick={() => { setQuota(null); setEmail(""); }}
                className="rounded-sm border-slate-200"
              >
                {t("paywall.back")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
